import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../context/AuthContext';

const TEMA = {
  bg: '#14161A',
  bgPanel: '#1B1D22',
  border: '#2A2B2E',
  ink: '#F0EEE8',
  inkSoft: '#9B9A93',
  inkFaint: '#6F6E68',
  accent: '#E0A83E',
  accentHover: '#F0BB56',
  onAccent: '#14161A',
  fel: '#D9695F',
};

function säkerställFonter() {
  if (document.getElementById('vinsales-fonter')) return;
  const preconnect1 = document.createElement('link');
  preconnect1.rel = 'preconnect';
  preconnect1.href = 'https://fonts.googleapis.com';
  const preconnect2 = document.createElement('link');
  preconnect2.rel = 'preconnect';
  preconnect2.href = 'https://fonts.gstatic.com';
  preconnect2.crossOrigin = 'anonymous';
  const stylesheet = document.createElement('link');
  stylesheet.id = 'vinsales-fonter';
  stylesheet.rel = 'stylesheet';
  stylesheet.href = 'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap';
  document.head.append(preconnect1, preconnect2, stylesheet);
}

// Supabase svarar på engelska. Översätt de vanliga fallen så att användaren
// förstår vad som behöver ändras istället för att bara se "försök igen".
function översättFel(meddelande: string): string {
  const m = (meddelande || '').toLowerCase();
  if (m.includes('should be different')) {
    return 'Det nya lösenordet måste skilja sig från det nuvarande.';
  }
  if (m.includes('weak') || m.includes('pwned') || m.includes('compromised') || m.includes('breach')) {
    return 'Lösenordet är för vanligt förekommande. Välj ett annat.';
  }
  if (m.includes('at least') || m.includes('characters')) {
    return 'Lösenordet är för kort.';
  }
  if (m.includes('expired') || m.includes('invalid') || m.includes('session')) {
    return 'Länken har gått ut. Begär en ny från inloggningssidan.';
  }
  return 'Kunde inte uppdatera lösenordet. Försök igen, eller begär en ny länk.';
}

function Öga({ synligt }: { synligt: boolean }) {
  return synligt ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      <path d="m4 20 16-16" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function AterstallLosenord() {
  const [länkGiltig, setLänkGiltig] = useState<boolean | null>(null);
  const [nyttLösenord, setNyttLösenord] = useState('');
  const [bekräftaLösenord, setBekräftaLösenord] = useState('');
  const [visaLösenord, setVisaLösenord] = useState(false);
  const [laddar, setLaddar] = useState(false);
  const [fel, setFel] = useState('');
  const [klart, setKlart] = useState(false);
  const [fokusFält, setFokusFält] = useState<'nytt' | 'bekräfta' | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    säkerställFonter();

    let harSessionen = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        harSessionen = true;
        setLänkGiltig(true);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && !harSessionen) {
        harSessionen = true;
        setLänkGiltig(true);
      }
    });

    // Om ingen recovery-session har etablerats inom rimlig tid är länken
    // ogiltig eller har redan använts — då visar vi ett tydligt felläge
    // istället för att låta formuläret hänga kvar overksamt.
    const timeout = setTimeout(() => {
      if (!harSessionen) setLänkGiltig(false);
    }, 2500);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  async function sättNyttLösenord(e: React.FormEvent) {
    e.preventDefault();
    setFel('');

    if (nyttLösenord.length < 8) {
      setFel('Lösenordet måste vara minst 8 tecken.');
      return;
    }
    if (nyttLösenord !== bekräftaLösenord) {
      setFel('Lösenorden matchar inte.');
      return;
    }

    setLaddar(true);
    const { error } = await supabase.auth.updateUser({ password: nyttLösenord });
    if (error) {
      console.error('Kunde inte uppdatera lösenordet:', error.message);
      setFel(översättFel(error.message));
      setLaddar(false);
    } else {
      setKlart(true);
      setLaddar(false);
    }
  }

  const inputStyle = (fält: 'nytt' | 'bekräfta'): React.CSSProperties => ({
    width: '100%',
    padding: '10px 42px 10px 12px',
    borderRadius: '2px',
    border: `1px solid ${fokusFält === fält ? TEMA.inkSoft : TEMA.border}`,
    fontSize: '14px',
    fontFamily: "'Inter', sans-serif",
    boxSizing: 'border-box',
    outline: 'none',
    background: TEMA.bg,
    color: TEMA.ink,
    transition: 'border-color 0.15s ease',
  });

  const ögonKnappStil: React.CSSProperties = {
    position: 'absolute',
    right: '4px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    padding: 0,
    background: 'none',
    border: 'none',
    color: TEMA.inkFaint,
    cursor: 'pointer',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: '11px',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: TEMA.accent,
    display: 'block',
    marginBottom: '6px',
  };

  const knappStil = (aktiv: boolean): React.CSSProperties => ({
    width: '100%', padding: '11px', borderRadius: '2px',
    background: TEMA.accent,
    color: TEMA.onAccent, fontSize: '14px',
    fontFamily: "'Inter', sans-serif",
    fontWeight: 500, border: `1px solid ${TEMA.accent}`, cursor: aktiv ? 'default' : 'pointer',
    transition: 'background 0.15s ease, opacity 0.15s ease',
    opacity: aktiv ? 0.7 : 1,
  });

  return (
    <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: TEMA.bg }}>
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem', width: '100%', maxWidth: '400px', padding: '0 1rem' }}>

        {/* Logotyp — samma lockup som hemsidan */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink, letterSpacing: '-0.01em' }}>
            Vin<em style={{ fontStyle: 'italic', color: TEMA.accent }}>sales</em>
          </span>
        </div>

        {/* Panel */}
        <div style={{
          background: TEMA.bgPanel,
          borderRadius: '12px',
          border: `1px solid ${TEMA.border}`,
          padding: '2rem',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          {länkGiltig === null && (
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, textAlign: 'center', margin: 0 }}>
              Verifierar länken...
            </p>
          )}

          {länkGiltig === false && (
            <>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, margin: '0 0 1.5rem', textAlign: 'center' }}>
                Länken är ogiltig eller har gått ut
              </p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.ink, textAlign: 'center', margin: '0 0 1.5rem' }}>
                Återställningslänkar slutar gälla efter en stund av säkerhetsskäl. Begär en ny länk från inloggningssidan.
              </p>
              <button type="button" onClick={() => navigate('/login')} style={knappStil(false)}>
                Till inloggning
              </button>
            </>
          )}

          {länkGiltig === true && !klart && (
            <>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, margin: '0 0 1.5rem', textAlign: 'center' }}>
                Välj ett nytt lösenord
              </p>

              <form onSubmit={sättNyttLösenord}>
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>Nytt lösenord</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={visaLösenord ? 'text' : 'password'}
                      value={nyttLösenord}
                      onChange={e => { setNyttLösenord(e.target.value); setFel(''); }}
                      onFocus={() => setFokusFält('nytt')}
                      onBlur={() => setFokusFält(null)}
                      style={inputStyle('nytt')}
                      placeholder="Minst 8 tecken"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setVisaLösenord(v => !v)}
                      aria-label={visaLösenord ? 'Dölj lösenordet' : 'Visa lösenordet'}
                      title={visaLösenord ? 'Dölj lösenordet' : 'Visa lösenordet'}
                      style={ögonKnappStil}
                      onMouseEnter={e => { e.currentTarget.style.color = TEMA.inkSoft; }}
                      onMouseLeave={e => { e.currentTarget.style.color = TEMA.inkFaint; }}
                    >
                      <Öga synligt={visaLösenord} />
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={labelStyle}>Bekräfta lösenord</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={visaLösenord ? 'text' : 'password'}
                      value={bekräftaLösenord}
                      onChange={e => { setBekräftaLösenord(e.target.value); setFel(''); }}
                      onFocus={() => setFokusFält('bekräfta')}
                      onBlur={() => setFokusFält(null)}
                      style={inputStyle('bekräfta')}
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setVisaLösenord(v => !v)}
                      aria-label={visaLösenord ? 'Dölj lösenordet' : 'Visa lösenordet'}
                      title={visaLösenord ? 'Dölj lösenordet' : 'Visa lösenordet'}
                      style={ögonKnappStil}
                      onMouseEnter={e => { e.currentTarget.style.color = TEMA.inkSoft; }}
                      onMouseLeave={e => { e.currentTarget.style.color = TEMA.inkFaint; }}
                    >
                      <Öga synligt={visaLösenord} />
                    </button>
                  </div>
                </div>

                {fel && (
                  <p style={{ fontFamily: "'Inter', sans-serif", color: TEMA.fel, fontSize: '13px', margin: '0 0 1rem', textAlign: 'center' }}>{fel}</p>
                )}

                <button
                  type="submit"
                  disabled={laddar}
                  style={knappStil(laddar)}
                  onMouseEnter={e => { if (!laddar) e.currentTarget.style.background = TEMA.accentHover; }}
                  onMouseLeave={e => { if (!laddar) e.currentTarget.style.background = TEMA.accent; }}
                >
                  {laddar ? 'Sparar...' : 'Spara nytt lösenord'}
                </button>
              </form>
            </>
          )}

          {klart && (
            <>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.inkSoft, margin: '0 0 1.5rem', textAlign: 'center' }}>
                Lösenordet är uppdaterat
              </p>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.ink, textAlign: 'center', margin: '0 0 1.5rem' }}>
                Du är inloggad och kan fortsätta direkt. Nästa gång loggar du in med det nya lösenordet.
              </p>
              <button type="button" onClick={() => navigate('/idag')} style={knappStil(false)}>
                Öppna Vinsales
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
